import type { Breakpoint } from "@mui/material/styles"
import type { NavSectionProps } from "src/components/nav-section"

import { useRef, useState } from "react"
import { Iconify } from "@/src/components/iconify"
import { uploadCompanyLogo } from "@/src/actions/company"

import Box from "@mui/material/Box"
import { useTheme } from "@mui/material/styles"
import { Tooltip, IconButton, CircularProgress } from "@mui/material"

import { varAlpha, hideScrollY } from "src/theme/styles"

import { Logo } from "src/components/logo"
import { Scrollbar } from "src/components/scrollbar"
import { NavSectionMini, NavSectionVertical } from "src/components/nav-section"

import { NavToggleButton } from "../components/nav-toggle-button"
import { toast } from "sonner"
import { useAuthContext } from "@/src/auth/hooks/use-auth-context"
// ----------------------------------------------------------------------

export type NavVerticalProps = NavSectionProps & {
    isNavMini: boolean
    layoutQuery: Breakpoint
    onToggleNav: () => void
    slots?: {
        topArea?: React.ReactNode
        bottomArea?: React.ReactNode
        bottomAreaMini?: React.ReactNode
    }
}

export function NavVertical({ sx, data, slots, isNavMini, layoutQuery, onToggleNav, ...other }: NavVerticalProps) {
    const theme = useTheme()
    const [isUploading, setIsUploading] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const { user, setUser } = useAuthContext()

    const onUpload = () => {
        inputRef.current?.click()
    }

    const handleFileUpload = async (file: FileList | null) => {
        if (!file?.[0]) return
        const fileSize = (file[0].size ?? 0) / 1024
        if (fileSize > 1024) {
            toast.error("File size must be less than 1MB")
            return
        }
        setIsUploading(true)
        const resp = await uploadCompanyLogo(file[0])
        setIsUploading(false)
        if (resp.status === "success" && resp.data) {
            setUser({ company: resp.data })
        } else {
            toast.error("Failed to upload logo")
        }
    }

    const imageSection = () => {
        const isEditAllowed = user?.highestRole === "SUPER_ADMIN" || user?.highestRole === "ADMIN"
        return (
            <Box sx={{ position: "relative" }}>
                <Logo />
                {isEditAllowed && (
                    <Tooltip title="Upload company logo">
                        <Box
                            onClick={onUpload}
                            sx={{
                                display: "flex",
                                position: "absolute",
                                top: 0,
                                left: 0,
                                height: "100%",
                                aspectRatio: "1/1",
                                opacity: isUploading ? 1 : 0,
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                justifyContent: "center",
                                alignItems: "center",
                                transition: "opacity 0.3s ease-in-out",
                                borderRadius: "2px",
                                cursor: "pointer",
                                "&:hover": {
                                    opacity: 1,
                                },
                            }}
                        >
                            <IconButton>
                                {isUploading ? (
                                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                                ) : (
                                    <Iconify icon="material-symbols:upload" color="#fff" />
                                )}
                            </IconButton>
                        </Box>
                    </Tooltip>
                )}
            </Box>
        )
    }

    const renderNavVertical = (
        <>
            {slots?.topArea ?? <Box sx={{ pl: 3.5, pt: 2.5, pb: 1 }}>{imageSection()}</Box>}

            <Scrollbar fillContent>
                <NavSectionVertical data={data} sx={{ px: 2, flex: "1 1 auto" }} {...other} />

                {!!slots?.bottomArea && slots?.bottomArea}
            </Scrollbar>
        </>
    )

    const renderNavMini = (
        <>
            {slots?.topArea ?? <Box sx={{ display: "flex", justifyContent: "center", py: 2.5 }}>{imageSection()}</Box>}

            <NavSectionMini
                data={data}
                sx={{ pb: 2, px: 0.5, ...hideScrollY, flex: "1 1 auto", overflowY: "auto" }}
                {...other}
            />

            {!!slots?.bottomAreaMini && slots.bottomAreaMini}
        </>
    )

    return (
        <Box
            sx={{
                top: 0,
                left: 0,
                height: 1,
                display: "none",
                position: "fixed",
                flexDirection: "column",
                bgcolor: "var(--layout-nav-bg)",
                zIndex: "var(--layout-nav-zIndex)",
                width: isNavMini ? "var(--layout-nav-mini-width)" : "var(--layout-nav-vertical-width)",
                borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey["500Channel"], 0.12)})`,
                transition: theme.transitions.create(["width"], {
                    easing: "var(--layout-transition-easing)",
                    duration: "var(--layout-transition-duration)",
                }),
                [theme.breakpoints.up(layoutQuery)]: {
                    display: "flex",
                },
                ...sx,
            }}
        >
            <NavToggleButton
                isNavMini={isNavMini}
                onClick={onToggleNav}
                sx={{
                    display: "none",
                    [theme.breakpoints.up(layoutQuery)]: {
                        display: "inline-flex",
                    },
                }}
            />
            {isNavMini ? renderNavMini : renderNavVertical}
            <input
                ref={inputRef}
                type="file"
                style={{ display: "none" }}
                onChange={(event) => handleFileUpload(event.target.files)}
                multiple
                accept=".svg,.jpg,.jpeg,.png"
            />
        </Box>
    )
}
